import React, { forwardRef, useCallback, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { FadeInBox } from '@components/Transitions'
import { ImageVariant, useImageSource } from '@components/UploadImage/useImageSource'
import { Box, BoxProps } from '@ui'
import { clsx } from 'clsx'
import { AnimatePresence } from 'framer-motion'
import { useGreenDot } from 'hooks/useGreenDot'
import { useResolveNft } from 'hooks/useNfts'
import { shimmerClass } from 'ui/styles/globals/shimmer.css'
import { type Address, useBotData, useImageStore, useUserLookup } from 'use-towns-client'

import { Icon, IconProps } from '../../ui/components/Icon'
import { MotionBox } from '../../ui/components/Motion/MotionComponents'

import {
    AvatarAtoms,
    avatarAtoms,
    avatarBaseStyle,
    avatarImageStyle,
    avatarToggleClasses,
    hexagonMask,
} from './Avatar.css'

type Props = {
    src?: string
    animate?: boolean
    type?: 'user' | 'space'
    onClick?: BoxProps['onClick']
    insetX?: BoxProps['insetX']
    insetY?: BoxProps['insetY']
    insetTop?: BoxProps['insetTop']
    insetBottom?: BoxProps['insetBottom']
    insetLeft?: BoxProps['insetLeft']
    insetRight?: BoxProps['insetRight']
    inset?: BoxProps['inset']
    boxShadow?: BoxProps['boxShadow']
    children?: React.ReactNode
    userId?: string
    icon?: IconProps['type']
    iconSize?: IconProps['size']
    imageVariant?: ImageVariant
    alt?: string
    lazy?: boolean
    dontAnimateGreenDot?: boolean
    isBot?: boolean
} & Omit<AvatarAtoms, 'circle' | 'square'> &
    Pick<BoxProps, 'tooltip' | 'tooltipOptions'>

export type AvatarProps = Props

// Lightweight placeholder component that shows shimmer animation
const PlaceholderAvatar = forwardRef<HTMLElement, Props>(
    (props: Props, ref: React.ForwardedRef<HTMLElement>) => {
        const {
            animate = false,
            size = 'avatar_md',
            height = 'avatar_md',
            type = 'user',
            stacked = false,
            border,
            icon,
            tooltip,
            tooltipOptions,
            onClick,
            iconSize,
            ...boxProps
        } = props

        const Container = animate ? MotionBox : Box

        const content = useMemo(() => {
            if (icon) {
                return (
                    <Box centerContent horizontal height="100%" background="level4">
                        <Icon type={icon} color="gray2" size={iconSize} />
                    </Box>
                )
            }
            return null
        }, [icon, iconSize])

        return (
            <Container
                display="block"
                ref={ref}
                shrink={false}
                position="relative"
                className={avatarAtoms({
                    size: size ?? height,
                })}
                {...boxProps}
            >
                <Box
                    absoluteFill
                    overflow="hidden"
                    tooltip={tooltip}
                    tooltipOptions={tooltipOptions}
                    onClick={onClick}
                    {...boxProps}
                    className={clsx(
                        avatarToggleClasses({
                            stacked,
                            border,
                            circle: type === 'user' && !props.isBot,
                        }),
                        shimmerClass,
                        avatarAtoms({
                            size: size ?? height,
                        }),
                        avatarBaseStyle,
                        props.isBot ? hexagonMask : undefined,
                    )}
                >
                    {content}
                </Box>
            </Container>
        )
    },
)

function useAvatarImageSrc(props: { imageVariant?: ImageVariant; userId?: string }) {
    const { imageVariant = 'thumbnail100', userId } = props
    const { imageSrc } = useImageSource(userId ?? '', imageVariant)

    return { imageSrc }
}

export const Avatar = forwardRef<HTMLElement, Props>((props, ref) => {
    const { lazy = false, ...restProps } = props

    // If not lazy, render the full avatar immediately
    if (!lazy) {
        return <FullAvatar {...restProps} ref={ref} />
    }

    // If lazy, use intersection observer to determine when to load
    return <LazyAvatar {...restProps} ref={ref} />
})

// Component that handles lazy loading logic
const LazyAvatar = forwardRef<HTMLElement, Props>(
    (props: Props, ref: React.ForwardedRef<HTMLElement>) => {
        const { ref: inViewRef, inView } = useInView({
            triggerOnce: true,
            rootMargin: '50px',
        })

        // Combine refs
        const combinedRef = useCallback(
            (node: HTMLElement | null) => {
                inViewRef(node)
                if (typeof ref === 'function') {
                    ref(node)
                } else if (ref) {
                    ref.current = node
                }
            },
            [ref, inViewRef],
        )

        // Show placeholder until avatar becomes visible
        if (!inView) {
            return <PlaceholderAvatar {...props} ref={combinedRef} />
        }

        // Once visible, render the full avatar
        return <FullAvatar {...props} ref={combinedRef} />
    },
)

// Component that runs all expensive operations
const FullAvatar = forwardRef<HTMLElement, Props>(
    (props: Props, ref: React.ForwardedRef<HTMLElement>) => {
        const isGreen = useGreenDot(props.userId)
        return <AvatarWithoutDot {...props} dot={props.isBot ? false : isGreen} ref={ref} />
    },
)

// pass a src prop to force using that image, otherwise pass userId
export const AvatarWithoutDot = forwardRef<HTMLElement, Props & { dot?: boolean }>((props, ref) => {
    const { src, userId, imageVariant, ...rest } = props

    const user = useUserLookup(userId ?? '')
    const _imageVariant = imageVariant ?? 'thumbnail100'

    const resolvedNft = useResolveNft({
        walletAddress: userId ?? '',
        info: user?.nft,
    })

    const nftUrl = useMemo(() => {
        if (!resolvedNft) {
            return
        }

        const image = resolvedNft.image
        if (image && image.thumbnailUrl) {
            return image.thumbnailUrl
        }
        // we had this check for bytes size, but what should we do if payload has no bytes?
        // if (resolvedNft.image.bytes && resolvedNft.image.bytes < MAX_NFT_IMAGE_SIZE) {
        //         return resolvedNft.image.gateway
        // }
        // there could be no bytes
        if (image && image.originalUrl) {
            return image.originalUrl
        }
    }, [resolvedNft])

    const { imageSrc } = useAvatarImageSrc({
        imageVariant: _imageVariant,
        userId,
    })
    const { imageSrc: botImageSrc } = useBotAvatar({ appAddress: user?.appAddress })

    useEffect(() => {
        if (src && userId) {
            console.warn(
                'Avatar: You passed both a src and userId prop. Only the src prop will be used.',
            )
        }
    }, [src, userId])

    const resourceId = useMemo(() => {
        if (!userId) {
            return ''
        }
        if (nftUrl) {
            return userId + '_' + nftUrl
        }
        return userId
    }, [userId, nftUrl])

    return (
        <_Avatar
            key={imageSrc}
            resourceId={resourceId}
            src={botImageSrc ?? nftUrl ?? src ?? imageSrc}
            userId={userId}
            {...rest}
            ref={ref}
        />
    )
})

const useBotAvatar = (props: { appAddress?: Address }) => {
    const { appAddress } = props
    const { data: botData } = useBotData(appAddress)
    return { imageSrc: botData?.metadata?.avatarUrl }
}

const _Avatar = forwardRef<
    HTMLElement,
    Props & {
        resourceId: string | undefined
        dot?: boolean
        dontAnimateGreenDot?: boolean
    }
>((props, ref) => {
    const {
        animate = false,
        size = 'avatar_md',
        height = 'avatar_md',
        type = 'user',
        stacked = false,
        border,
        src,
        resourceId = '',
        icon,
        dot: _dot,
        tooltip,
        tooltipOptions,
        onClick,
        iconSize,
        userId,
        dontAnimateGreenDot = false,
        isBot = false,
        alt,
        ...boxProps
    } = props

    const Container = animate ? MotionBox : Box

    const { setLoadedResource, addErroredResource } = useImageStore((state) => ({
        setLoadedResource: state.setLoadedResource,
        addErroredResource: state.addErroredResource,
    }))
    const onError = useCallback(() => {
        if (resourceId) {
            addErroredResource(resourceId)
        }
    }, [addErroredResource, resourceId])

    const onLoad = useCallback(() => {
        if (resourceId && src) {
            setLoadedResource(resourceId, {
                imageUrl: src,
            })
        }
    }, [resourceId, setLoadedResource, src])

    const failedResource = useImageStore((state) => state.erroredResources[resourceId])
    const loadedResource = useImageStore((state) => state.loadedResource[resourceId])

    const content = useMemo(() => {
        if (icon) {
            return (
                <Box centerContent horizontal height="100%" background="level4">
                    <Icon type={icon} color="gray2" size={iconSize} />
                </Box>
            )
        }
        if (src && !userId) {
            return <img alt={alt ?? ''} src={src} className={avatarImageStyle} />
        }

        //initial image load - this should render for each resourceId once to save the failed/loaded state so subsequent instances don't need to check
        if (src && !failedResource && !loadedResource) {
            return (
                <img
                    alt={alt ?? ''}
                    src={src}
                    className={avatarImageStyle}
                    style={{
                        opacity: 0,
                    }}
                    onLoad={onLoad}
                    onError={onError}
                />
            )
        }

        if (loadedResource) {
            return <img alt={alt ?? ''} src={src} className={avatarImageStyle} />
        }
    }, [failedResource, icon, iconSize, loadedResource, onError, onLoad, src, userId, alt])
    const dot = useMemo(() => {
        if (!_dot) {
            return null
        }
        const Component = dontAnimateGreenDot ? Box : FadeInBox
        return (
            <Component
                borderRadius="full"
                style={{
                    position: `absolute`,
                    right: `0%`,
                    bottom: `0%`,
                    width: `100%`,
                    height: `100%`,
                    maxWidth: `var(--dot-width, 50px)`,
                    maxHeight: `var(--dot-width, 50px)`,
                    borderColor: `var(--background)`,
                    borderWidth: `var(--dot-border-width)`,
                    borderStyle: `solid`,
                    transformOrigin: `center center`,
                    transform: ` rotate(45deg) translateX(var(--dot-offset, 50%)) scale(0.37)`,
                }}
            >
                <Box absoluteFill background="presence" borderRadius="full" />
            </Component>
        )
    }, [_dot, dontAnimateGreenDot])

    const fallbackImage = useMemo(
        () => `url(/placeholders/${userId ? getFallbackIcon(userId) : 'pp1'}.png)`,
        [userId],
    )

    return (
        <Container
            display="block"
            ref={ref}
            shrink={false}
            position="relative"
            className={avatarAtoms({
                size: size ?? height,
            })}
            {...boxProps}
        >
            <Box
                absoluteFill
                overflow="hidden"
                tooltip={tooltip}
                tooltipOptions={tooltipOptions}
                onClick={onClick}
                {...boxProps}
                className={clsx(
                    avatarToggleClasses({ stacked, border, circle: type === 'user' && !isBot }),
                    undefined,
                    avatarAtoms({
                        size: size ?? height,
                    }),
                    avatarBaseStyle,
                    isBot ? hexagonMask : undefined,
                )}
                style={{
                    // all users should have a image uploaded but in the case they somehow don't, or fails to load, we use a placeholder image
                    backgroundImage: failedResource ? fallbackImage : 'none',
                }}
            >
                {content}
            </Box>
            <AnimatePresence>{dot}</AnimatePresence>
        </Container>
    )
})

function getFallbackIcon(userId: string): string {
    // pick last byte of address and convert to number, then mod 25 to get a
    // number between 1-25 (based on iOS version)
    const index = (parseInt(userId.slice(-2), 16) % 25) + 1
    return `pp${index}`
}
